import React, { useEffect } from 'react';
import DynamicContentField from './stage/DynamicContentField';
import { ContentItem, Activity, PainPoint, Opportunity } from '@/types/journey';

interface StageContentProps {
  description?: string; // Kept for backward compatibility
  painPoints?: string;
  opportunities?: string;
  contentItems: ContentItem[];
  onDescriptionChange?: (value: string) => void; // Kept for backward compatibility
  onPainPointsChange?: (value: string) => void;
  onOpportunitiesChange?: (value: string) => void;
  onContentUpdate: (contentItems: ContentItem[]) => void;
}

const StageContent: React.FC<StageContentProps> = ({
  description,
  painPoints,
  opportunities,
  contentItems,
  onDescriptionChange,
  onPainPointsChange,
  onOpportunitiesChange,
  onContentUpdate
}) => {
  // Initialize contentItems from legacy fields if missing
  useEffect(() => {
    const newContentItems = [...contentItems];
    let hasChanges = false;
    
    // Check for description
    if (description && !contentItems.some(item => item.type === 'description')) {
      newContentItems.push({
        id: `content-description-${Date.now()}`,
        type: 'description',
        content: description
      });
      hasChanges = true;
    }
    
    // Check for pain points
    if (painPoints && !contentItems.some(item => item.type === 'painPoints')) {
      // Convert string-based painPoints to array of objects
      const painPointsArray: PainPoint[] = painPoints.split('\n')
        .filter(text => text.trim())
        .map(text => ({
          id: `pain-point-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          text: text.trim()
        }));
      
      if (painPointsArray.length > 0) {
        newContentItems.push({
          id: `content-painPoints-${Date.now()}`,
          type: 'painPoints',
          content: painPointsArray
        });
        hasChanges = true;
      }
    }
    
    // Check for opportunities
    if (opportunities && !contentItems.some(item => item.type === 'opportunities')) {
      // Convert string-based opportunities to array of objects
      const opportunitiesArray: Opportunity[] = opportunities.split('\n')
        .filter(text => text.trim())
        .map(text => ({
          id: `opportunity-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          text: text.trim()
        }));
      
      if (opportunitiesArray.length > 0) {
        newContentItems.push({
          id: `content-opportunities-${Date.now()}`,
          type: 'opportunities',
          content: opportunitiesArray
        });
        hasChanges = true;
      }
    }
    
    // If we've added any items, update the content
    if (hasChanges) {
      onContentUpdate(newContentItems);
    }
  }, [description, painPoints, opportunities, contentItems, onContentUpdate]);
  
  // Handle content updates
  const handleContentUpdate = (updatedContentItems: ContentItem[]) => {
    onContentUpdate(updatedContentItems);
  };
  
  return (
    <DynamicContentField 
      contentItems={contentItems}
      onContentUpdate={handleContentUpdate}
    />
  );
};

export default StageContent;
